import { atoms, STORAGE_PREFIX } from "./Events/Recoil";
import { appStore } from "./store";

/**
 * Atoms that hold "current booking flow" state and should be wiped when
 * the user starts a brand new flow (search, change-date, cancel, etc.).
 *
 * EXCLUDED on purpose:
 *   - Auth/identity: userDataAtom, tokenStateAtom, isAuthenticatedStateAtom,
 *     customerContactStateAtom — these legitimately span flows.
 *   - Resume signals: paymentStateAtom — needed for refresh-during-payment.
 *   - Reference data: contentDetailAtom, isAnonymousStateAtom — not flow state.
 *
 * The list mirrors the booking-flow groupings in WhitelistedAtoms.ts plus a
 * handful of in-memory atoms that aren't whitelisted but still leak across
 * SPA navigation between flows.
 */
const BOOKING_FLOW_ATOM_KEYS: readonly string[] = [
  // Booking response & trips
  "bookingResponseAtom",
  // After-sales (upgrade / change-date / quick-exchange) booking response.
  // WHITELISTED for persistence (WhitelistedAtoms.ts) so a change-date
  // checkout survives the /checkout/cancel reload — see
  // sessionRefundLegs.ts's isCdtExchangeForBooking, which reads it to decide
  // whether to refund the leg's onward products. Because it now outlives a
  // refresh, wiping it here is the ONLY thing stopping a stale value from
  // arming that refund on an unrelated payment. Do not drop it from this list.
  // Its presence with `.booking.exchangeOperations` is also what makes Payment.tsx
  // show the "Exchange Terms & Conditions" confirmation before pay. If an
  // upgrade/change-date flow is entered then abandoned (e.g. logo -> new
  // search), a leftover value makes that after-sales confirmation pop up on
  // a brand-new booking's payment. Wipe it on every flow entry.
  "quickExchangeBookingResponseAtom",
  "selectedTripsDataAtom",
  "bookingRequestAtom",
  "selectedOfferIdAtom",
  "selectedOutboundTrainAtom",
  "selectedInboundTrainAtom",
  "selectedTrainStateAtom",
  "selectedInboundTrainStateAtom",

  // Passenger selection
  "selectedPassengersAtom",
  "selectedPassengersOutBoundAtom",
  "selectedPassengersInBoundAtom",
  "selectedPassengersPerTypeStateAtom",
  "passengerStateAtom",
  "displayPassengerDataAtom",
  "allPassengerPriceAndAgeGroupAtom",
  "allPassengerPriceAndAgeGroupInBoundAtom",

  // Payment items / summary (NOT paymentStateAtom — resume needs it)
  "paymentOutBoundItemsDetailsAtom",
  "paymentInboundItemsDetailsAtom",
  "checkoutPaymentSummaryAtom",
  "earnPointsTotalAtom",
  "earnExtraPointsAtom",

  // Seating
  "selectedSeatsAtom",
  "originalSeatsAtom",
  "confirmedSeatsAtom",
  "selectedCoachAtom",
  "placeMapResponseAtom",
  "coachLayoutResponseAtom",
  "floorPlanIconsResponseAtom",

  // Shuttle (in-memory + whitelisted)
  "trainShuttleBookingAtom",
  "outboundShuttleState",
  "inboundShuttleState",
  "selectedShuttlesAtom",
  "shuttleBookedOffersAtom",
  "isAddShuttleFlowAtom",
  // Onward travel added-entries (in-memory only — no localStorage key to purge).
  // Global and side-keyed, so an entry left by an
  // abandoned checkout — or seeded from a viewed booking by
  // useHydrateBookedShuttles — used to reach confirmOnwardSelection as `prev` on
  // the NEXT booking and refuse the add outright. That guard now drops an entry
  // absent from the current booking, so this reset is defence in depth: it stops
  // a foreign entry being carried and rendered in the first place.
  "shuttleAddedAtom",
  "nolAddedAtom",

  // Cancel-booking flow context
  "selectedPassengerForCancelAtom",
  "selectedPassengerForCancelInBoundAtom",
  "selectedBookingIdForCancelAtom",
  "tripDisableForCancelSelectionAtom",
  "cancelBookingConfirmPopupFlagAtom",
  "selectedBookingTripStatusAtom",
  // Derived booking status of the last-viewed booking (FULFILLED / REFUNDED /
  // PAST), set by GetBookingDetailStatusAction on the booking-details page.
  // In-memory only (not whitelisted). After a cancel, it holds "REFUNDED";
  // PrepareBookingSummaryAction reads it to set otherDetails.isRefund, which
  // flips the checkout total line label from "Total to pay" to "Refunded
  // Amount". Without wiping it on flow entry, that stale "REFUNDED" leaks into
  // the next brand-new booking's checkout. UnmountBookingDetailAction clears it
  // too, but doesn't fire on every nav path out of details.
  "bookingConfirmStatusAtom",

  // Refund / change-date offers
  "refundOfferResponseAtom",
  "refundFeeAtom",
  "refundableAmountAtom",

  // Manage-seating refund-on-pay queue (whitelisted -> persists across SPA nav,
  // so wipe on flow entry to keep stale IDs from a prior abandoned manage-seating
  // session out of an unrelated flow's payment-success callback).
  "pendingRefundFulfillmentIdsAtom",
  // Per-ancillary refund quote cache populated by ProbeRefundOffersAction at
  // manage-seating entry. Same persistence concern as the queue above: wipe on
  // flow entry so a stale quote from an abandoned session can't be confirmed
  // against an unrelated booking at pay-time.
  "refundOffersByFulfillmentIdAtom",

  // Trip-offer display (NOT searchStateAtom / inboundSearchStateAtom —
  // those hold the active user-facing search criteria, written by upstream
  // selection actions BEFORE SearchButton.Click and read by the train-offer
  // page header. Wiping them between click and navigation paints the header
  // empty.)
  "selectedDateAtom",
  "inboundSelectedDateAtom",
  "offerAtom",
  "tripOfferAtom",
  "nextAndPreviousTrainAtom",
  "defaultActiveKeyForTripSummaryAtom",
  "tripLegsDetailsAtom",
  "selectedTrainLegsAtom",
  "filterTrainsAtom",
  "visibleTrainsAtom",

  // Per-flow mode flags. SearchInitStep already resets these on every
  // search, but cancel / change-date / book-return-journey entries don't
  // run that step — so a stale flag from a previous abandoned flow can
  // poison getAddonsFromBookingAction (forces PREBOOKED-only filter that
  // wipes FULFILLED offers, see PrepareBookingSummaryAction.ts:930).
  "isChangeDateStateAtom",
  "isBookReturnJourneyAtom",
  "isUpgradeToPremiumStateAtom",
  "isQuickExchangeStateAtom",
  "isManageSeatingAtom",

  // Checkout-side, flow-specific (NOT auth/contact)
  "pendingBookingDataAtom",
  "guestCheckoutBookingIdAtom",
  "checkoutUserDataAtom",
  "confirmAddonsPassengerListAtom",
  // Change-date carry-over fingerprints. Wiped on every fresh flow entry
  // so a stale snapshot from an aborted change-date attempt can't bleed
  // into another booking's "NEW" tag logic.
  "changeDateCarryOverSnapshotAtom",
  "savedPassengerDetailAtom",
  "passengerMappingAtom",
  "areAllPassengerDetailsCompleteAtom",

  // Misc per-flow flags
  "isHyperFastStateAtom",
];

/**
 * Reset every booking-flow atom to its declared default and remove any
 * persisted entry from localStorage. Call at the START of every "new
 * booking flow" entry point (search submit, change-date click, etc.) so
 * a previous aborted flow can't bleed into the next one.
 *
 * Does NOT touch identity atoms (user, token, contact) or paymentStateAtom
 * (used by the refresh-during-payment resume path). Safe to call multiple
 * times — each call is a no-op against atoms already at their defaults.
 */
export function clearBookingFlowAtoms(): void {
  // Tests routinely jest.mock("…/Events/Recoil") which makes `atoms`
  // undefined here. Skip rather than crash so action tests still run; the
  // helper itself is covered by clearBookingFlowAtoms.test.ts against the
  // real registry. Warn in dev so a real production breakage doesn't go
  // silent.
  if (!Array.isArray(atoms)) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(
        "[clearBookingFlowAtoms] atoms registry unavailable; skipping (likely mocked test env)"
      );
    }
    return;
  }
  for (const key of BOOKING_FLOW_ATOM_KEYS) {
    const atomRef = atoms.find((a) => a.key === key);
    if (atomRef) {
      // `initialValue` is declared optional on the type (atoms added directly
      // to the registry without going through `atomWithKey` won't have it).
      // Guard so we only restore when the default is actually known; the
      // localStorage removal below still runs so the persisted value is at
      // least cleared even if the in-memory atom can't be reset.
      if ("initialValue" in atomRef) {
        appStore.set(atomRef, atomRef.initialValue);
      }
    }
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
      } catch {
        // localStorage unavailable (private mode, quota); ignore.
      }
    }
  }
}
